import { array, option } from "fp-ts";
import { Option } from "fp-ts/lib/Option";
import { pipe } from "fp-ts/lib/function";

export interface IQueryService {
  setPrefix(prefix: string): void;
  get(key: string): Option<string>;
  set(key: string, value: string): void;
  remove(key: string): void;
  getArray(key: string): Option<string[]>;
  setArray(key: string, values: string[]): void;
}

export class NothingQueryService implements IQueryService {
  get(key: string): option.Option<string> {
    return option.none;
  }
  set(key: string, value: string): void {}
  setPrefix(prefix: string): void {}
  remove(key: string): void {}
  getArray(key: string): option.Option<string[]> {
    return option.none;
  }
  setArray(key: string, values: string[]): void {}
}

export class QueryService implements IQueryService {
  prefix: string = "";
  constructor(prefix: string = "") {
    this.setPrefix(prefix);
  }

  setPrefix(prefix: string): void {
    if (prefix.length == 0) this.prefix = "";
    else this.prefix = `${prefix}_`;
  }

  get(key: string): Option<string> {
    const urlSearchParams = QueryService.getQueryParams();
    return option.fromNullable(urlSearchParams.get(this.prefix + key));
  }

  set(key: string, value: string): void {
    const urlSearchParams = QueryService.getQueryParams();
    urlSearchParams.set(this.prefix + key, value);
    QueryService.updateQueryParams(urlSearchParams);
  }

  remove(key: string): void {
    const urlSearchParams = QueryService.getQueryParams();
    urlSearchParams.delete(this.prefix + key);
    QueryService.updateQueryParams(urlSearchParams);
  }

  getArray(key: string): Option<string[]> {
    return pipe(
      this.get(key),
      option.map((s) => s.split(",").map((value) => decodeURIComponent(value)))
    );
  }

  setArray(key: string, values: string[]): void {
    if (array.isEmpty(values)) {
      this.remove(key);
    } else {
      this.set(key, values.map(QueryService.encodeUriString).join(","));
    }
  }

  private static getQueryParams(): URLSearchParams {
    return new URLSearchParams(window.location.search);
  }

  private static updateQueryParams(params: URLSearchParams): void {
    const newParamsString = params.toString();
    const currentParamsString = QueryService.getQueryParams().toString();
    if (newParamsString === currentParamsString) {
      return; // skip update since the params are already in the requested state
    }

    window.history.replaceState(
      {},
      "",
      QueryService.decodeUriString(
        `${window.location.pathname}?${params}${window.location.hash}`
      )
    );
  }

  private static encodeUriString(s: string): string {
    return encodeURIComponent(s).replace(/%20/g, "+");
  }
  private static decodeUriString(s: string): string {
    return decodeURIComponent(s.replace(/\+/g, "%20"));
  }
}
